/**
 * 「사흘」의 2026 년 소품.
 *
 * 이 마을은 1998 년에 문을 닫은 분교를 청년 여섯이 이 년 전에 고쳐서 들어온 곳이다.
 * 그래서 **골조는 90 년대고 사는 사람은 지금 사람이다.** 그 대비가 안 보이면
 * 플레이어는 이 게임을 시대극으로 읽는다 — 실제로 그런 말을 들었다.
 *
 * 대비를 만드는 것은 건물이 아니라 **책상 위에 널린 것들**이다. 낡은 교실 벽에
 * 기대 놓인 삼각대, 칠판 밑의 택배 상자, 나무 마루 위의 공유기 불빛.
 *
 * 그리고 그중에서도 **켜진 화면 하나가 나머지 전부보다 세다.** 어두운 방에
 * 발광하는 사각형이 있으면 그 방은 즉시 지금이 된다. 그래서 화면에 무엇이
 * 떠 있는지를 사람마다 다르게 그린다 — 화면이 곧 그 사람이다.
 *
 * 치수는 실물에서 가져왔다. 27 인치 모니터의 가로는 0.62 m 고, 택배 상자
 * 3 호는 0.35×0.25×0.21 m 다. 눈대중으로 넣으면 방에 놨을 때 바로 틀어진다.
 */

import * as THREE from 'three';
import { bevelBox, lathe, shellBox, type MeshWriter } from './kernel';
import { paintGeometry, type Piece } from './pieces';

/* ------------------------------------------------------------------ */
/* 화면                                                                */
/* ------------------------------------------------------------------ */

/** 화면 재질 하나. 텍스처가 곧 발광면이라 emissiveMap 과 map 을 같이 물린다 */
function screenMat(draw: (g: CanvasRenderingContext2D, w: number, h: number) => void, w = 512, h = 320) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d')!;
  g.fillStyle = '#0d1014';
  g.fillRect(0, 0, w, h);
  draw(g, w, h);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return new THREE.MeshStandardMaterial({
    map: tex,
    emissiveMap: tex,
    // 화면은 스스로 빛나야 한다. 방 조명을 껐을 때 유일하게 남는 것이 이것이다
    emissive: new THREE.Color('#ffffff'),
    emissiveIntensity: 1.15,
    roughness: 0.22,
    metalness: 0,
    vertexColors: true,
  });
}

/**
 * 영상 편집 타임라인.
 *
 * 차유리는 이 마을의 기록을 찍어 온 사람이다. 그 사람 방의 화면에는 이게 떠 있다.
 * **트랙이 여러 줄이고 클립이 잘게 쪼개져 있는 모양**만으로 편집 프로그램이 읽힌다.
 * 글자를 쓸 필요가 없다 — 27 인치를 1 m 밖에서 보면 어차피 안 읽힌다.
 */
export function editTimeline(g: CanvasRenderingContext2D, w: number, h: number) {
  // 위쪽 절반은 프리뷰 창
  g.fillStyle = '#16191f';
  g.fillRect(0, 0, w, h * 0.52);
  g.fillStyle = '#000';
  g.fillRect(w * 0.16, h * 0.04, w * 0.68, h * 0.42);
  // 프리뷰 안의 그림 — 눈 내리는 밤의 마을. 무엇을 찍고 있었는지가 보여야 한다
  const grd = g.createLinearGradient(0, h * 0.04, 0, h * 0.46);
  grd.addColorStop(0, '#1d2740');
  grd.addColorStop(1, '#39424e');
  g.fillStyle = grd;
  g.fillRect(w * 0.16, h * 0.04, w * 0.68, h * 0.42);
  g.fillStyle = '#0e1218';
  g.fillRect(w * 0.16, h * 0.33, w * 0.68, h * 0.13);
  g.fillStyle = 'rgba(255,255,255,0.55)';
  for (let i = 0; i < 40; i++) {
    const x = w * 0.16 + Math.random() * w * 0.68;
    const y = h * 0.04 + Math.random() * h * 0.42;
    g.fillRect(x, y, 1.4, 1.4);
  }

  // 아래쪽은 타임라인. 트랙 네 줄
  g.fillStyle = '#101319';
  g.fillRect(0, h * 0.52, w, h * 0.48);
  const trackH = h * 0.1;
  const colors = ['#3f6ea8', '#3f6ea8', '#4d8a5c', '#6a5a8c'];
  for (let t = 0; t < 4; t++) {
    const y = h * 0.55 + t * trackH;
    g.fillStyle = '#181c23';
    g.fillRect(w * 0.06, y, w * 0.92, trackH - 3);
    let x = w * 0.06;
    while (x < w * 0.98) {
      const cw = w * (0.03 + Math.random() * 0.1);
      g.fillStyle = colors[t];
      g.fillRect(x, y + 1, Math.min(cw, w * 0.98 - x) - 2, trackH - 6);
      // 오디오 트랙은 파형을 그린다
      if (t === 2) {
        g.fillStyle = 'rgba(255,255,255,0.35)';
        for (let s = 0; s < cw; s += 3) {
          const a = Math.random() * (trackH - 10);
          g.fillRect(x + s, y + (trackH - a) / 2, 1.5, a);
        }
      }
      x += cw;
    }
  }
  // 재생 헤드. 빨간 세로선 하나가 「지금 멈춰 있다」를 만든다
  g.fillStyle = '#e2483c';
  g.fillRect(w * 0.42, h * 0.52, 2, h * 0.48);
}

/** 흔한 창 몇 개가 겹친 바탕화면. 누구 것인지 특정하지 않는 기본값 */
export function genericDesktop(g: CanvasRenderingContext2D, w: number, h: number) {
  g.fillStyle = '#1b2029';
  g.fillRect(0, 0, w, h);
  for (const [x, y, ww, hh, c] of [
    [0.06, 0.1, 0.5, 0.55, '#252c37'],
    [0.2, 0.22, 0.55, 0.6, '#2c3440'],
  ] as const) {
    g.fillStyle = c;
    g.fillRect(w * x, h * y, w * ww, h * hh);
    g.fillStyle = '#39424f';
    g.fillRect(w * x, h * y, w * ww, h * 0.05);
  }
  g.fillStyle = 'rgba(190,205,225,0.5)';
  for (let i = 0; i < 16; i++) {
    g.fillRect(w * 0.23, h * 0.31 + i * h * 0.03, w * (0.1 + Math.random() * 0.4), 2);
  }
  // 작업 표시줄
  g.fillStyle = '#12161c';
  g.fillRect(0, h * 0.93, w, h * 0.07);
}

/**
 * 본체 측면 강화유리 — 유리와 그 속을 한 장에 그린다.
 *
 * 어두운 판 위에 팬 두 개가 돌고 그래픽카드가 가로로 누워 있다. 발광은 팬 테두리
 * 뿐이라 emissive 를 약하게 준다. 화면(screen)처럼 세게 빛나면 방 안에서 모니터랑
 * 구별이 안 된다 — 본체는 「어둠 속에서 은은하게 숨 쉬는 것」이어야 한다.
 */
function pcSidePanel(): THREE.MeshStandardMaterial {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 256;
  const g = c.getContext('2d')!;
  g.fillStyle = '#0e1013';
  g.fillRect(0, 0, 256, 256);

  // 메인보드 — 판보다 아주 조금 밝은 사각형
  g.fillStyle = '#171b20';
  g.fillRect(28, 24, 200, 150);

  // 그래픽카드. 가로로 누운 두꺼운 막대 하나가 「요즘 컴퓨터」를 만든다
  g.fillStyle = '#22262c';
  g.fillRect(40, 122, 180, 34);
  g.fillStyle = '#2b3038';
  g.fillRect(40, 122, 180, 6);

  // 팬 둘. 테두리 링이 빛나고 날개는 어둡다
  for (const [cx, cy, r] of [
    [92, 200, 34],
    [176, 200, 34],
  ] as const) {
    g.fillStyle = '#101318';
    g.beginPath();
    g.arc(cx, cy, r, 0, Math.PI * 2);
    g.fill();
    const ring = g.createRadialGradient(cx, cy, r * 0.72, cx, cy, r);
    ring.addColorStop(0, 'rgba(70,200,220,0)');
    ring.addColorStop(0.7, 'rgba(70,200,220,0.85)');
    ring.addColorStop(1, 'rgba(120,140,240,0.5)');
    g.fillStyle = ring;
    g.beginPath();
    g.arc(cx, cy, r, 0, Math.PI * 2);
    g.fill();
    // 날개 일곱
    g.strokeStyle = 'rgba(150,190,220,0.28)';
    g.lineWidth = 4;
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2;
      g.beginPath();
      g.moveTo(cx + Math.cos(a) * r * 0.2, cy + Math.sin(a) * r * 0.2);
      g.arc(cx, cy, r * 0.78, a, a + 0.5);
      g.stroke();
    }
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return new THREE.MeshStandardMaterial({
    map: tex,
    emissiveMap: tex,
    emissive: new THREE.Color('#ffffff'),
    emissiveIntensity: 0.42,
    roughness: 0.18,
    metalness: 0.1,
    side: THREE.DoubleSide,
    vertexColors: true,
  });
}

/* ------------------------------------------------------------------ */
/* 부품                                                                */
/* ------------------------------------------------------------------ */

/**
 * 화면 한 장. **-Z 를 본다.**
 *
 * 이 프로젝트의 가구는 전부 -Z 가 정면이다(협탁 서랍이 -d/2 에 달려 있다).
 * PlaneGeometry 는 +Z 를 보므로 뒤집어야 한다. 안 뒤집으면 카탈로그에서도
 * 방에서도 모니터 뒤통수만 보인다 — 실제로 그렇게 만들어 놓고 한참 헤맸다.
 */
function panel(w: MeshWriter, width: number, height: number) {
  const g = new THREE.PlaneGeometry(width, height);
  g.rotateY(Math.PI);
  w.thinSurface(() => w.add(g, 'screen', 0));
}

/**
 * 케이블 한 가닥. 늘어진 곡선이라 직선으로 그리면 즉시 가짜가 된다.
 *
 * 관(TubeGeometry)은 양 끝이 뚫려 있다. **양 끝을 반드시 다른 solid 안에 묻어라** —
 * 기기 몸통 속과 바닥 밑이다. 지름 8mm 짜리 구멍이라 밖에서는 안 보이고, 그래서
 * 닫힘 검사에서 뺀다(thinSurface). 끝을 허공에 두면 그때는 진짜로 뚫려 보인다.
 */
function cable(w: MeshWriter, from: [number, number, number], to: [number, number, number], sag = 0.12) {
  const a = new THREE.Vector3(...from);
  const b = new THREE.Vector3(...to);
  const mid = a.clone().lerp(b, 0.5);
  mid.y -= sag;
  const curve = new THREE.QuadraticBezierCurve3(a, mid, b);
  w.thinSurface(() => w.add(new THREE.TubeGeometry(curve, 12, 0.004, 5, false), 'rubber', 0.6));
}

/* ------------------------------------------------------------------ */

export const SAHOL_PIECES: Piece[] = [
  {
    id: 'monitor',
    mount: 'surface',
    name: '모니터',
    collection: 'sahol',
    category: '2026',
    note: '27인치 · 얇은 베젤 · 켜져 있다. 화면 텍스처를 갈아 끼워 사람마다 다른 것을 띄운다.',
    /** 책상 위에 놓는 물건이다 — 전원선이 책상 아래로 늘어진다 */
    sunk: true,
    dims: { w: 0.62, h: 0.36, standH: 0.11, tilt: 0.06 },
    /* 화면 각도를 조절한다 — 패널이 목 위에서 앞뒤로 젖힌다 (2026-09-16) */
    openable: [{ part: '화면', swing: 0.3, axis: 'x', pivot: [0, 0.17, 0] }],
    build(w, d) {
      w.part('패널');
      w.movable('화면', () => {
      w.at(0, d.standH + 0.06 + d.h / 2, 0, () => {
        // 뒤판은 +Z 쪽이다. **화면 평면보다 앞까지 나와야 한다** — 뒤판 앞면과
        // 화면 사이에 1mm 라도 빈틈이 있으면 옆에서 볼 때 그 틈으로 속이 비친다
        const back = bevelBox(d.w, d.h, 0.02, 0.003);
        back.translate(0, 0, 0.008);
        w.add(back, 'plastic', 0.5);
        // 베젤 — 요즘 모니터는 3mm 다. 두꺼우면 2010 년대로 내려간다
        w.at(0, 0, -0.0025, () => panel(w, d.w - 0.006, d.h - 0.006));
      });
      });

      w.part('목');
      const neck = bevelBox(0.07, 0.16, 0.05, 0.004);
      neck.translate(0, d.standH + 0.02, 0.03);
      w.add(neck, 'alu', 0.4);

      w.part('받침');
      const foot = bevelBox(0.22, 0.014, 0.17, 0.004);
      foot.translate(0, 0.007, 0.03);
      w.add(foot, 'alu', 0.4);

      w.part('전원선');
      // 끝을 y = -0.012 로 내린다. 바닥 밑이라 방에서는 안 보이고, 허공에서 끝나면
      // 8mm 짜리 관 구멍이 그대로 보인다
      cable(w, [0, d.standH + 0.02, 0.05], [0.16, -0.012, 0.14], 0.05);
    },
    materials: () => ({ screen: screenMat(editTimeline) }),
  },

  {
    id: 'laptop_open',
    mount: 'surface',
    name: '열린 노트북',
    collection: 'sahol',
    category: '2026',
    note: '14인치 · 상판이 뒤로 105도. 덮여 있으면 그냥 알루미늄 판이라 열어 둔다.',
    // 105도 = 1.833 rad. **부호가 + 다** — 처음에 -1.86 을 넣었더니 상판이
    // 책상 아래로 누워서 「닫힌 노트북」처럼 보였다
    dims: { w: 0.31, d: 0.22, deckT: 0.014, lidT: 0.006, lidA: 1.833 },
    /* 상판은 뒤(+Z) 경첩에서 105° 열려 구워 있다. E 를 누르면 덮인다 — swing 이 음수라
     * 구워진 각을 상쇄해 자판 위로 내려앉는다 */
    openable: [{ part: '상판', swing: -1.83, axis: 'x', pivot: [0, 0.014, 0.11] }],
    build(w, d) {
      w.part('본체');
      const deck = bevelBox(d.w, d.deckT, d.d, 0.002);
      deck.translate(0, d.deckT / 2, 0);
      w.add(deck, 'alu', 0.35);

      // **자판은 키가 보여야 한다.** 판 한 장(2mm)으로 두면 위에서 봤을 때 노트북이
      // 아니라 회색 사각형이 붙은 알루미늄 판이다 — 여섯 방향 시트에서 그렇게 보였다.
      // 키를 격자로 찍고 아래에 터치패드를 둔다. 개별 키는 베벨 없는 박스라 12삼각형씩,
      // 8×4 = 32키에 384삼각형이면 예산에 여유가 있다.
      w.part('자판');
      const kw = d.w - 0.05;
      const kd = d.d * 0.42;
      const kz = -d.d * 0.16;
      const cols = 8;
      const rows = 4;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const key = new THREE.BoxGeometry(kw / cols - 0.004, 0.0026, kd / rows - 0.004);
          key.translate(
            -kw / 2 + (kw / cols) * (c + 0.5),
            d.deckT + 0.0013,
            kz - kd / 2 + (kd / rows) * (r + 0.5),
          );
          w.add(key, 'plastic', 0.3);
        }
      }
      w.part('터치패드');
      const pad = bevelBox(d.w * 0.34, 0.0016, d.d * 0.22, 0.001);
      pad.translate(0, d.deckT + 0.0008, d.d * 0.22);
      w.add(pad, 'plastic', 0.25);

      w.part('상판');
      // 경첩은 뒤쪽(+Z) 모서리다. **판을 돌린 뒤 경첩만큼 옮긴다** — 원점에서
      // 돌리면 상판이 본체 한가운데를 뚫고 선다
      w.movable('상판', () => w.at(0, d.deckT, d.d / 2, () => {
        const lidD = d.d * 0.96;
        const m = new THREE.Matrix4().makeRotationX(d.lidA);
        const off = new THREE.Vector3(0, 0, -lidD / 2).applyMatrix4(m);

        const back = bevelBox(d.w, d.lidT, lidD, 0.002);
        back.applyMatrix4(m);
        back.translate(off.x, off.y, off.z);
        w.add(back, 'alu', 0.35);

        // 화면은 상판의 **아랫면**이다 — 닫으면 자판을 마주 보는 쪽. 그래서
        // 평면을 -Y 로 눕혀 놓고 상판과 같은 각으로 같이 돌린다
        const s = new THREE.PlaneGeometry(d.w - 0.014, lidD - 0.014);
        s.rotateX(Math.PI / 2);
        s.translate(0, -d.lidT / 2 - 0.001, 0);
        s.applyMatrix4(m);
        s.translate(off.x, off.y, off.z);
        w.thinSurface(() => w.add(s, 'screen', 0));
      }));
    },
    materials: () => ({ screen: screenMat(genericDesktop) }),
  },

  {
    id: 'desk_pc',
    name: '데스크탑 본체',
    collection: 'sahol',
    category: '2026',
    note: '미들타워 · 측면 강화유리 · 앞면 발광 띠. 책상 밑에서 이것만 살아 있다.',
    dims: { w: 0.21, h: 0.45, d: 0.44 },
    /* 강화유리 옆판이 뒤로 밀려 빠진다 — 실제 미들타워 패널처럼 +Z(후면)로 당기면 나온다 */
    openable: [{ part: '옆판', slide: [0, 0, 0.3], inside: { w: 0.16, h: 0.38, d: 0.36, at: [0, 0.23, 0] } }],
    build(w, d) {
      w.part('몸통');
      const body = bevelBox(d.w, d.h, d.d, 0.004);
      body.translate(0, d.h / 2, 0);
      w.add(body, 'plastic', 0.55);

      w.part('앞면 통풍구');
      // 가로 슬릿 여섯. 앞판이 민짜면 그냥 검은 상자다
      for (let i = 0; i < 6; i++) {
        const slit = bevelBox(d.w - 0.05, 0.008, 0.004, 0.001);
        slit.translate(0, d.h * 0.62 + i * 0.022, -d.d / 2 - 0.0015);
        w.add(slit, 'steel', 0.3);
      }

      w.part('발광 띠');
      // 앞면 세로 한 줄. **이게 본체를 2026 년으로 만든다** — 90 년대 PC 에는 없다
      const strip = new THREE.PlaneGeometry(0.012, d.h * 0.72);
      strip.rotateY(Math.PI);
      strip.translate(-d.w / 2 + 0.022, d.h * 0.42, -d.d / 2 - 0.002);
      w.thinSurface(() => w.add(strip, 'screen', 0));

      w.part('측면 유리');
      // 오른쪽(+X)이 강화유리다.
      //
      // 처음에는 진짜 투명 재질(transmission) 뒤에 발광하는 팬 판을 넣었는데,
      // **아무것도 안 비쳤다.** 투과 재질은 씬을 한 번 더 그려야 뒤가 보이는데
      // 이 파이프라인은 그걸 안 돌린다. 그래서 유리와 그 속을 **한 장에 그린다** —
      // 어차피 책상 옆 1m 에서 보는 물건이라 진짜 굴절은 아무도 못 알아본다.
      const glass = new THREE.PlaneGeometry(d.d - 0.05, d.h - 0.06);
      glass.rotateY(Math.PI / 2);
      glass.translate(d.w / 2 + 0.0015, d.h / 2, 0);
      w.movable('옆판', () => w.thinSurface(() => w.add(glass, 'glass', 0)));

      w.part('발');
      for (const sx of [-1, 1]) {
        for (const sz of [-1, 1]) {
          const f = bevelBox(0.03, 0.016, 0.03, 0.002);
          f.translate((sx * (d.w - 0.05)) / 2, 0.008, (sz * (d.d - 0.06)) / 2);
          w.add(f, 'rubber', 0.3);
        }
      }
    },
    // 발광 띠는 흰빛이 아니다. 차가운 청록이 요즘 케이스의 기본값이다.
    // 유리는 「어두운 판 + 그 속에서 도는 팬」을 통째로 그린 한 장이다
    materials: () => ({
      screen: screenMat((g, w2, h2) => {
        const grd = g.createLinearGradient(0, 0, 0, h2);
        grd.addColorStop(0, '#3fd8e0');
        grd.addColorStop(0.5, '#5a8cf0');
        grd.addColorStop(1, '#7a5ae0');
        g.fillStyle = grd;
        g.fillRect(0, 0, w2, h2);
      }, 16, 64),
      glass: pcSidePanel(),
    }),
  },

  {
    id: 'router',
    mount: 'surface',
    nofront: true,
    name: '공유기',
    collection: 'sahol',
    category: '2026',
    note: '안테나 둘 · LED 셋. 산골이라 이게 있어야 사람이 사는 집으로 읽힌다.',
    /** 책상·선반 위에 놓는 물건이다 — 랜선이 아래로 늘어진다 */
    sunk: true,
    dims: { w: 0.19, d: 0.13, h: 0.032, antH: 0.13 },
    /* 공유기를 통째로 들어 뒤를 본다 — 랜선째 (2026-09-16) */
    openable: [{ part: '공유기', slide: [0, 0, -0.05] }],
    build(w, d) {
      w.movable('공유기', () => {
      w.part('몸통');
      const body = bevelBox(d.w, d.h, d.d, 0.004);
      body.translate(0, d.h / 2, 0);
      w.add(body, 'plastic', 0.5);

      w.part('표시등');
      // 켜진 점 셋. 작지만 어두운 방에서 이것만 살아 있다. 앞면(-Z)에 붙는다
      for (let i = 0; i < 3; i++) {
        const led = new THREE.PlaneGeometry(0.008, 0.008);
        led.rotateX(-Math.PI / 2);
        led.translate(-0.05 + i * 0.018, d.h + 0.0006, -d.d / 2 + 0.02);
        w.thinSurface(() => w.add(led, 'screen', 0));
      }

      w.part('안테나');
      // 안테나는 뒤(+Z)로 뺀다. 앞으로 뻗으면 표시등을 가린다
      for (const [sx, tilt] of [
        [-1, 0.42],
        [1, -0.3],
      ] as const) {
        const a = bevelBox(0.012, d.antH, 0.006, 0.002);
        const m = new THREE.Matrix4().makeRotationZ(tilt);
        a.translate(0, d.antH / 2, 0);
        a.applyMatrix4(m);
        a.translate((sx * d.w) / 2 - sx * 0.012, d.h, d.d / 2 - 0.02);
        w.add(a, 'plastic', 0.4);
      }

      w.part('랜선');
      cable(w, [0.03, 0.01, d.d / 2], [0.2, -0.012, 0.24], 0.03);
      });
    },
    // 표시등은 초록 점이다. 편집 타임라인을 여기 물리면 안 된다
    materials: () => ({
      screen: screenMat((g, w2, h2) => {
        g.fillStyle = '#39d17a';
        g.fillRect(0, 0, w2, h2);
      }, 8, 8),
    }),
  },

  {
    id: 'parcel',
    nofront: true,
    name: '택배 상자',
    collection: 'sahol',
    category: '2026',
    /* 뚜껑이 오른쪽으로 한 폭만큼 미끄러진다. 담는 곳은 상자 속(w 0.35 · h 0.21 · d 0.25) */
    openable: [
      { part: '뚜껑', slide: [0.3, 0, 0], inside: { w: 0.3, h: 0.16, d: 0.2, at: [0, 0.1, 0] } },
    ],
    note:
      '3호 상자 · 송장 붙어 있음. 눈에 갇힌 마을이 바깥과 이어진 유일한 물건이다.\n' +
      '**테이프가 성한 상자다** (2026-09-11, Chris: 「테이프 없는 상자는 알아서 옆으로 슥 밀리게」) — ' +
      '반품 박스처럼 갈려 양옆으로 벌어지는 게 아니라 **뚜껑이 통째로 옆으로 미끄러진다**. ' +
      '그래서 뚜껑을 `w.movable` 로 따로 두고 몸통은 위가 트인 상자로 만든다.',
    dims: { w: 0.35, d: 0.25, h: 0.21, t: 0.006, /** 포장 속(완충지·비닐에 싼 물건). 0 이면 빈 상자 */ packed: 1 },
    build(w, d) {
      /* 위가 트인 몸통 — 옆판 넷 + 바닥. 통짜로 두면 뚜껑을 밀어도 속이 안 보인다 */
      w.part('상자');
      const walls: [number, number, number, number, number, number][] = [
        [0, d.h / 2, -d.d / 2 + d.t / 2, d.w, d.h, d.t],
        [0, d.h / 2, d.d / 2 - d.t / 2, d.w, d.h, d.t],
        [-d.w / 2 + d.t / 2, d.h / 2, 0, d.t, d.h, d.d - d.t * 2],
        [d.w / 2 - d.t / 2, d.h / 2, 0, d.t, d.h, d.d - d.t * 2],
        [0, d.t / 2, 0, d.w, d.t, d.d],
      ];
      for (const [x, y, z, bw, bh, bd] of walls) {
        const g = bevelBox(bw, bh, bd, 0.003);
        g.translate(x, y, z);
        w.add(g, 'cardboard', 0.55);
      }

      /* 뚜껑 — 판 한 장에 테이프 한 줄. 옆(+x)으로 미끄러진다 */
      w.movable('뚜껑', () => {
        const lid = bevelBox(d.w + 0.004, d.t * 1.6, d.d + 0.004, 0.003);
        lid.translate(0, d.h + d.t * 0.8, 0);
        w.add(lid, 'cardboard', 0.55);
        // 가운데 한 줄. 이게 없으면 그냥 갈색 상자다
        const tape = bevelBox(0.05, 0.0016, d.d + 0.012, 0.0004);
        tape.translate(0, d.h + d.t * 1.6 + 0.0008, 0);
        w.add(tape, 'plastic', 0.3);
      });

      w.part('송장');
      const label = new THREE.PlaneGeometry(0.1, 0.07);
      label.rotateY(Math.PI); // 정면은 -Z
      label.translate(0, d.h * 0.62, -d.d / 2 - 0.0012);
      w.thinSurface(() => w.add(label, 'paper', 0.4));

      /* **속** (2026-09-16, Chris: 「빈 상자 금지」) — 비닐 완충재에 싼 물건 하나가 뒤쪽 벽에 기대 있고,
       * 구긴 완충지 뭉치가 앞을 채운다. 가운데는 낮게 둬 담는 곳(`inside`)에 넣은 물건이 위에 앉는다 */
      if ((d.packed ?? 1) > 0) {
        w.part('완충재');
        const iw = d.w - d.t * 2, id = d.d - d.t * 2;
        const item = bevelBox(iw * 0.42, d.h * 0.55, id * 0.42, 0.012, 2);
        item.rotateY(0.18);
        item.translate(-iw * 0.24, d.t + d.h * 0.275, id * 0.2);
        paintGeometry(item, '#cfd9e2');   // 비닐 완충재 — 회색 돌덩이로 읽히지 않게 밝게
        w.add(item, 'paint', 0.5);
        const wads: [number, number, number, number][] = [[iw * 0.28, id * 0.2, 0.046, 0.7], [iw * 0.22, -id * 0.22, 0.042, 2.1], [-iw * 0.2, -id * 0.24, 0.038, 3.4], [iw * 0.0, -id * 0.05, 0.032, 1.2], [-iw * 0.34, -id * 0.02, 0.03, 2.6]];
        for (const [x, z, r, a] of wads) {
          const g = new THREE.IcosahedronGeometry(r, 0);
          g.scale(1.25, 0.72, 1);
          g.rotateY(a); g.rotateX(a * 0.3);
          g.translate(x, d.t + r * 0.72, z);
          w.add(g, 'paper', 0.3);
        }
      }
    },
  },

  {
    id: 'tripod',
    nofront: true,
    name: '삼각대와 카메라',
    collection: 'sahol',
    category: '2026',
    note: '다리 셋을 벌려 세운 삼각대와 미러리스 카메라. 사진 찍는 사람의 방·작업실 구석에.',
    dims: { legH: 1.15, spread: 0.34, camW: 0.13 },
    /* 삼각대에서 카메라를 떼어 든다 (2026-09-16) */
    openable: [{ part: '카메라', slide: [0, 0.03, -0.04] }],
    build(w, d) {
      w.part('다리');
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        const fx = Math.cos(a) * d.spread;
        const fz = Math.sin(a) * d.spread;
        const top = new THREE.Vector3(0, d.legH, 0);
        const foot = new THREE.Vector3(fx, 0, fz);
        const dir = foot.clone().sub(top);
        const len = dir.length();
        const leg = bevelBox(0.022, len, 0.022, 0.003);
        leg.translate(0, -len / 2, 0);
        const q = new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(0, -1, 0),
          dir.clone().normalize(),
        );
        leg.applyMatrix4(new THREE.Matrix4().makeRotationFromQuaternion(q));
        leg.translate(top.x, top.y, top.z);
        w.add(leg, 'alu', 0.35);
      }

      w.part('기둥');
      const col = bevelBox(0.032, 0.18, 0.032, 0.003);
      col.translate(0, d.legH + 0.09, 0);
      w.add(col, 'alu', 0.35);

      w.part('카메라');
      w.movable('카메라', () => {
      const body = bevelBox(d.camW, 0.082, 0.06, 0.004);
      body.translate(0, d.legH + 0.22, 0);
      w.add(body, 'plastic', 0.45);
      // 렌즈. 원기둥 하나로 「카메라」가 확정된다
      const lens = lathe(
        [
          [0.026, 0],
          [0.031, 0.012],
          [0.031, 0.07],
          [0.028, 0.078],
          [0, 0.078],
        ],
        16,
      );
      const m = new THREE.Matrix4().makeRotationX(Math.PI / 2);
      lens.applyMatrix4(m);
      lens.translate(0, d.legH + 0.22, -0.03);
      w.add(lens, 'plastic', 0.3);
      });
    },
  },

  {
    id: 'mug',
    mount: 'surface',
    nofront: true,
    name: '머그컵',
    collection: 'sahol',
    category: '2026',
    note: '손잡이 달린 것. 로스터리가 있는 마을이라 어느 방에나 하나씩 굴러다닌다.',
    dims: { r: 0.042, h: 0.095 },
    /* 머그를 집어 든다 (2026-09-16) */
    openable: [{ part: '머그컵', slide: [0, 0, -0.05] }],
    build(w, d) {
      w.movable('머그컵', () => {
      w.part('컵');
      w.add(
        lathe(
          [
            [0, 0],
            [d.r * 0.86, 0],
            [d.r * 0.9, 0.006],
            [d.r * 0.96, d.h * 0.5],
            [d.r, d.h],
            [d.r - 0.004, d.h],
            [d.r - 0.005, 0.008],
            [0, 0.008],
          ],
          18,
        ),
        'porcelain',
        0.5,
      );
      w.part('손잡이');
      /* **손잡이는 C 자 고리다** (2026-09-10, Chris: 「머그컵 손잡이 다시」).
       * 예전엔 **완전한 도넛을 옆면에 붙여** 두어 컵 밖으로 나오질 않았다 — 손가락이 들어갈 데가 없는
       * 「옆구리에 붙은 고리」였다. 컵 벽에서 나와 바깥으로 휘었다가 다시 벽으로 붙어야 한다.
       * 고리 면은 컵 축을 품은 세로 평면(XY)에 있고 바깥으로 반지름의 60% 넘게 나온다.
       * 단면 타원을 베지어 길로 밀어내면 양 끝 캡이 닫혀 몸체도 닫힌다(api.md 작은 부품 레시피). */
      const sec = new THREE.Shape();
      /* 단면은 **반지름 방향으로 두껍고 앞뒤로 얇다**(13 × 8.4mm) — 손가락이 걸리는 쪽이 두꺼워야 한다.
       * 밀어내기 길의 프레네 프레임에서 Shape 의 x 는 곡선이 놓인 평면 안(=반지름 방향), y 는 그 평면의
       * 법선(=앞뒤)으로 간다. 처음엔 이걸 뒤집어 써서 앞뒤로 뚱뚱한 소시지가 됐다. */
      sec.absellipse(0, 0, 0.0065, 0.0042, 0, Math.PI * 2, false, 0);
      /* 부착점은 컵 벽 **속**이다. 벽은 안지름 37 · 바깥 38~41.5mm 로 위가 벌어지므로 38mm 로 잡으면
       * 위아래 모두 벽 두께 안에 들어간다. 위는 림 바로 아래(0.86h), 아래는 0.2h — 세로 62mm 라
       * 손가락 세 개가 들어간다. 예전엔 세로 42mm 라 옆에 붙은 귀처럼 보였다. */
      /* 컵은 아래로 좁아진다. 위아래 부착 x 를 같게 두면 위는 3.5mm 묻히는데 아래는 0.6mm 라
       * 아래 끝이 옆으로 뻗다 만 것처럼 보였다. **그 높이의 실제 외벽에서 1mm 안쪽**으로 각각 잡는다. */
      const wall = (y: number) =>
        y >= d.h * 0.5
          ? d.r * 0.96 + d.r * 0.04 * ((y - d.h * 0.5) / (d.h * 0.5))
          : d.r * 0.9 + d.r * 0.06 * ((y - 0.006) / (d.h * 0.5 - 0.006));
      const yTop = d.h * 0.86;
      const yBot = d.h * 0.24;
      const xTop = wall(yTop) - 0.001;
      const xBot = wall(yBot) - 0.001;
      const hpath = new THREE.CubicBezierCurve3(
        new THREE.Vector3(xTop, yTop, 0),
        new THREE.Vector3(xTop + 0.046, yTop, 0),
        new THREE.Vector3(xBot + 0.048, yBot, 0),
        new THREE.Vector3(xBot, yBot, 0),
      );
      const handle = new THREE.ExtrudeGeometry(sec, { extrudePath: hpath, steps: 14, bevelEnabled: false, curveSegments: 5 });
      w.add(handle, 'porcelain', 0.5);
      });
    },
  },

  {
    id: 'softbox',
    nofront: true,
    name: '촬영 조명',
    collection: 'sahol',
    category: '2026',
    note: '스탠드 + 사각 소프트박스. 방에 세워 두면 그 방이 즉시 촬영장이 된다.',
    dims: { poleH: 1.45, boxW: 0.5, boxH: 0.5, depth: 0.26 },
    /* 조명 스탠드를 통째로 끌어 옮긴다 (2026-09-16) */
    openable: [{ part: '조명', slide: [0.1, 0, 0] }],
    build(w, d) {
      w.movable('조명', () => {
      w.part('다리');
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2 + 0.5;
        const foot = new THREE.Vector3(Math.cos(a) * 0.28, 0, Math.sin(a) * 0.28);
        const top = new THREE.Vector3(0, 0.4, 0);
        const dir = foot.clone().sub(top);
        const len = dir.length();
        const leg = bevelBox(0.018, len, 0.018, 0.002);
        leg.translate(0, -len / 2, 0);
        const q = new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(0, -1, 0),
          dir.clone().normalize(),
        );
        leg.applyMatrix4(new THREE.Matrix4().makeRotationFromQuaternion(q));
        leg.translate(top.x, top.y, top.z);
        w.add(leg, 'steel', 0.4);
      }
      w.part('기둥');
      const pole = bevelBox(0.026, d.poleH, 0.026, 0.003);
      pole.translate(0, d.poleH / 2, 0);
      w.add(pole, 'steel', 0.4);

      w.part('소프트박스');
      // **닫힌 사각뿔대**로 만든다. 처음에는 작은 상자 앞에 큰 판 한 장을 세웠는데,
      // 판 뒤가 텅 비어서 뒤에서 보면 실루엣의 57%가 비쳤다(검수 0.4302).
      // 4각 실린더는 위아래 뚜껑이 있어 그 자체로 닫혀 있다.
      const half = Math.SQRT1_2; // 한 변 s 인 정사각형의 외접원 반지름 = s/√2
      const shell = new THREE.CylinderGeometry(
        (d.boxW / 2) / half, // 앞면(넓은 쪽)
        (d.boxW / 2) * 0.5 / half, // 뒷면(좁은 쪽)
        d.depth,
        4,
      );
      shell.rotateY(Math.PI / 4); // 모서리가 아니라 면이 정면을 보게
      // 축을 Y 에서 Z 로. **넓은 쪽(rTop)이 -Z 를 봐야 한다** — 부호를 뒤집었더니
      // 확산천이 좁은 쪽에 붙어서 뒤에서 실루엣의 26%가 비쳤다(검수 0.7376)
      shell.rotateX(-Math.PI / 2);
      shell.translate(0, d.poleH + d.boxH * 0.2, d.depth / 2);
      w.add(shell, 'plastic', 0.5);

      w.part('디퓨저');
      // 확산천. 뿔대 앞면보다 1mm 앞에 둔다
      const face = new THREE.PlaneGeometry(d.boxW, d.boxH);
      face.rotateY(Math.PI);
      face.translate(0, d.poleH + d.boxH * 0.2, -0.001);
      w.thinSurface(() => w.add(face, 'paint', 0.5));
      });
    },
  },

  {
    id: 'clothes_pile',
    glb: '/models/sahol/clothes_pile',   // 블렌더 GLB (tools/blender/props.py). 아래 build 는 대체이자 크기 계약
    nofront: true,
    name: '쌓아 둔 옷',
    collection: 'sahol',
    category: '2026',
    note: '개키지 않고 얹어 놓은 더미. 사흘째 같은 옷을 입은 사람의 방에 있어야 하는 것.',
    dims: { w: 0.44, d: 0.34, h: 0.2 },
    /* 더미 맨 위의 옷 한 벌을 집어 든다 (2026-09-16) */
    openable: [{ part: '옷 한 벌', slide: [0, 0, -0.08] }],
    build(w, d) {
      w.part('더미');
      // 납작한 덩어리 넷을 어긋나게 얹는다. 하나면 쿠션이고 넷이면 더미다
      for (let i = 0; i < 4; i++) {
        const t = i / 3;
        const g = bevelBox(
          d.w * (1 - t * 0.28),
          d.h * 0.3,
          d.d * (1 - t * 0.22),
          0.02,
          2,
        );
        g.rotateY((i - 1.5) * 0.34);
        g.translate((i - 1.5) * 0.035, d.h * 0.16 + i * d.h * 0.21, (i - 1.5) * 0.028);
        w.add(g, 'fabric', 0.8);
      }

      /* glb 조각이라 곁가지로 얹어 집어 드는 여닫이로 쓴다 (2026-09-16).
       * 더미 꼭대기에 반쯤 펼쳐 얹은 옷 — 더미 bbox 안에 둔다 */
      w.movable('옷 한 벌', () => {
        const g = bevelBox(d.w * 0.6, d.h * 0.2, d.d * 0.55, 0.02, 2);
        g.rotateY(-0.5);
        g.translate(0.02, d.h * 0.86, 0.03);
        w.add(g, 'fabric', 0.8);
      });
    },
  },
];

/** 옷더미 비교군 — 천 시뮬레이션으로 구운 GLB(tools/blender 밖, ~/blender-lab/clothes_cloth.py). 크기 계약은 clothes_pile 과 같다 */
SAHOL_PIECES.push({
  ...SAHOL_PIECES.find((p) => p.id === 'clothes_pile')!,
  id: 'clothes_pile_cloth',
  glb: '/models/sahol/clothes_pile_cloth',
  name: '쌓아 둔 옷 (천 시뮬)',
  /* 맨 윗옷을 들춰 더미 아래를 본다 — GLB 의 cloth0_f 노드가 맨 위 한 장 (2026-09-16 상호작용) */
  openable: [{ part: '윗옷', node: 'cloth0_f', slide: [0, 0.18, -0.14] }],
  note: '비교군. 천 다섯 장을 떨어뜨려 주름을 구운 것.',
});

/* ------------------------------------------------------------------ */
/* 수색 소품 (2026-09-18, Chris: 「다 만들어보자」)                      */
/*                                                                    */
/* 카탈로그에 들추는 장판·쌀통·재떨이·유선전화·구급함·브라운관이         */
/* 통째로 없었다 — 전부 「만지면 움직여서 아래·안·뒤가 보이는」 물건들로, */
/* 이 게임의 추리 자리(숨긴 것을 찾는 동작)가 되는 것들이다.             */
/* ------------------------------------------------------------------ */

/** 좌식 장판 — 뒤 모서리를 축으로 들어 넘겨 깔린 자리를 본다 */
SAHOL_PIECES.push({
  id: 'floor_mat',
  nofront: true,
  name: '좌식 장판',
  collection: 'sahol',
  category: '2026',
  note:
    '마루 위에 깐 돗자리·요. 밑에 떨어뜨린 것을 숨기는 가장 오래된 방법이라 ' +
    '수색은 장판을 들추는 데서 시작한다. 들면 안 되고 **넘겨야** 한다 — 들면 공중에 뜬 천이 된다.',
  dims: { w: 1.0, d: 1.45, t: 0.012 },
  /* 뒤 모서리(z +d/2)를 축으로 앞을 들어 뒤로 넘긴다. swing 을 π 가깝게 두어
   * 넘어간 장판이 원래 자리 뒤에 거의 납작하게 놓인다 — 이불 들추기와 같은 동작.
   * pivot 의 z 는 dims.d 의 절반(0.725) — openSpecFor 가 dims 비율로 같이 스케일한다. */
  openable: [{ part: '장판', swing: 3.0, axis: 'x', pivot: [0, 0, 0.725] }],
  build(w, d) {
    w.movable('장판', () => {
      w.part('장판');
      const mat = bevelBox(d.w, d.t, d.d, 0.005, 2);
      mat.translate(0, d.t / 2, 0);
      w.add(mat, 'fabric', 0.35);
      // 테두리 바이어스 띠 — 장판은 가장자리가 먼저 말리고 이 띠가 두께를 만든다
      const b = 0.035;
      const edge = (bw: number, bd: number, x: number, z: number) => {
        const g = bevelBox(bw, d.t * 1.4, bd, 0.003);
        g.translate(x, d.t / 2, z);
        paintGeometry(g, '#5f646e');
        w.add(g, 'paint', 0.2);
      };
      edge(d.w, b, 0, -d.d / 2 + b / 2);
      edge(d.w, b, 0, d.d / 2 - b / 2);
      edge(b, d.d - b * 2, -d.w / 2 + b / 2, 0);
      edge(b, d.d - b * 2, d.w / 2 - b / 2, 0);
      // 누빔 선 두 줄 — 맨짠 천이면 마루에 깐 널판지로 읽힌다
      w.thinSurface(() => {
        for (const zz of [-d.d * 0.17, d.d * 0.17]) {
          const seam = bevelBox(d.w - b * 2.5, 0.0015, 0.006, 0.0005);
          seam.translate(0, d.t + 0.0008, zz);
          paintGeometry(seam, '#7d828c');
          w.add(seam, 'paint', 0.1);
        }
      });
    });
  },
});

/** 쌀통 — 뚜껑을 뒤로 젖히면 채운 쌀이 보이고, 쌀 속을 헤집는 수색이 된다 */
SAHOL_PIECES.push({
  id: 'rice_bin',
  name: '쌀통',
  collection: 'sahol',
  category: '2026',
  note:
    '부엌 한켠의 플라스틱 곡식통. 뚜껑을 열면 쌀이 보여야 하고, 쌀을 헤집어 보는 게 부엌 수색의 기본이다. ' +
    '통이 깊으니 쌀 아래 다른 것이 묻혀 있어도 겉으로는 안 읽힌다.',
  dims: { w: 0.35, d: 0.26, h: 0.33, t: 0.006 },
  /* 뚜껑은 뒤 힌지(몸통 윗모서리 z +d/2)에서 뒤로 젖힌다 — 쌀통 뚜껑은 통째로 들리는 게 아니라
   * 뒤에 매달려 넘어간다. inside 는 쌀 속 — 묻힌 것을 에디터가 직접 넣을 수 있다. */
  openable: [
    { part: '뚜껑', swing: 1.95, axis: 'x', pivot: [0, 0.304, 0.13], inside: { w: 0.29, h: 0.2, d: 0.2, at: [0, 0.15, 0] } },
  ],
  build(w, d) {
    const bodyH = d.h - 0.026;   // 뚜껑 자리를 남긴 몸통 높이 (0.304)
    /* 쌀통은 밝은 플라스틱이다 — 어두운 plastic 슬롯 그대로 두면 냉장고 옆에서
     * 정체 불명의 검은 상자로 읽힌다 (2026-09-18, Chris: 「냉장고 앞에 이건 뭐임」) */
    const BODY = '#e7e2d4', LID = '#cfc9b8';
    w.part('통');
    for (const g of shellBox(d.w, bodyH, d.d, d.t, ['+y'])) {
      paintGeometry(g, BODY);
      w.add(g, 'paint', 0.3);
    }
    // 옆 손잡이 귀 — 쌀통은 양옆 귀가 있어야 옮기는 물건이 된다
    w.part('손잡이');
    for (const s of [-1, 1]) {
      const hd = bevelBox(0.014, 0.02, 0.07, 0.003);
      hd.translate(s * (d.w / 2 - 0.004), bodyH * 0.62, 0);
      paintGeometry(hd, BODY);
      w.add(hd, 'paint', 0.25);
    }

    // --- 채운 쌀 — 통 안 3/4 높이에 살짝 불룩하게. 뚜껑을 열었을 때 처음 보이는 것
    w.part('쌀');
    const rice = new THREE.SphereGeometry(1, 20, 10);
    rice.scale(d.w / 2 - d.t - 0.004, 0.05, d.d / 2 - d.t - 0.004);
    rice.translate(0, bodyH * 0.72, 0);
    paintGeometry(rice, '#ddd5be');
    w.add(rice, 'paint', 0.15);
    // 계량컵 — 쌀에 반쯤 박혀 비스듬히 손잡이를 드러낸다
    w.part('계량컵');
    const cup = new THREE.CylinderGeometry(0.034, 0.028, 0.045, 12);
    cup.rotateZ(0.55);
    cup.rotateX(-0.15);
    cup.translate(d.w * 0.16, bodyH * 0.72 + 0.045, 0.015);
    w.add(cup, 'plastic', 0.3);
    const cupH = new THREE.CylinderGeometry(0.005, 0.005, 0.085, 8);
    cupH.rotateZ(1.25);
    cupH.translate(d.w * 0.16 + 0.055, bodyH * 0.72 + 0.075, 0.015);
    w.add(cupH, 'plastic', 0.3);

    /* 뚜껑은 닫힌 채로 붙인다 — pivot(몸통 뒤 윗모서리)에서 뒤로 젖힌다 */
    w.movable('뚜껑', () => {
      const lid = bevelBox(d.w + 0.012, 0.026, d.d + 0.012, 0.006, 2);
      lid.translate(0, bodyH + 0.013, 0);
      paintGeometry(lid, LID);
      w.add(lid, 'paint', 0.3);
      // 앞 손잡이턱 — 뚜껑을 들어 여는 자리
      const grip = bevelBox(d.w * 0.36, 0.016, 0.035, 0.004);
      grip.translate(0, bodyH + 0.034, -d.d / 2 + 0.045);
      paintGeometry(grip, LID);
      w.add(grip, 'paint', 0.25);
    });
  },
});

/** 재떨이와 꽁초 — 누가 언제 몇 개비를 피웠는지가 남는 자리 */
SAHOL_PIECES.push({
  id: 'ashtray',
  mount: 'surface',
  nofront: true,
  name: '재떨이와 꽁초',
  collection: 'sahol',
  category: '2026',
  note:
    '도자기 재떨이에 눌린 꽁초와 걸친 담배. 방에 재떨이가 없는데 꽁초가 있으면 손님이 앉았던 곳이다. ' +
    '재떨이째 들어 옮겨 밑을 본다 — 머그와 같은 동작.',
  dims: { r: 0.055, h: 0.04 },
  /* 재떨이째 들어 옮겨 밑을 본다 (머그와 같은 slide) */
  openable: [{ part: '재떨이', slide: [0, 0, -0.055] }],
  build(w, d) {
    w.movable('재떨이', () => {
      w.part('받침');
      // 두툼한 접시 — 국물그릇을 뒤집은 프로파일에 끝이 안으로 말린 림
      const dish = lathe(
        [
          [0, 0],
          [d.r * 0.6, 0],
          [d.r * 0.82, 0.005],
          [d.r, d.h * 0.55],
          [d.r, d.h * 0.8],
          [d.r * 0.93, d.h],
          [d.r * 0.88, d.h * 0.8],
          [d.r * 0.88, d.h * 0.5],
          [d.r * 0.72, 0.013],
          [0, 0.013],
        ],
        20,
      );
      w.add(dish, 'porcelain', 0.4);
      // 재 — 가운데 회백색 봉분
      const ash = new THREE.SphereGeometry(d.r * 0.42, 12, 6);
      ash.scale(1, 0.28, 1);
      ash.translate(0, d.h * 0.6, 0);
      paintGeometry(ash, '#96948e');
      w.add(ash, 'paint', 0.1);

      /* 꽁초 세 개비 — 눌러 꺼진 담배. 흰 몸통 + 갈색 필터.
       * 상표는 안 그린다 (상표 금지) — 꽁초는 형태로 읽는다 */
      w.part('꽁초');
      const butt = (x: number, z: number, ang: number) => {
        const body = new THREE.CylinderGeometry(0.004, 0.004, 0.026, 8);
        body.rotateZ(Math.PI / 2 - 0.22);
        body.rotateY(ang);
        body.translate(x, d.h * 0.7, z);
        paintGeometry(body, '#e6e2d6');
        w.add(body, 'paint', 0.1);
        const filt = new THREE.CylinderGeometry(0.0043, 0.0043, 0.009, 8);
        filt.rotateZ(Math.PI / 2 - 0.22);
        filt.rotateY(ang);
        filt.translate(
          x - Math.cos(ang) * 0.016 * Math.cos(0.22),
          d.h * 0.7 - 0.016 * Math.sin(0.22),
          z + Math.sin(ang) * 0.016 * Math.cos(0.22),
        );
        paintGeometry(filt, '#b08a4f');
        w.add(filt, 'paint', 0.1);
      };
      butt(0.012, -0.012, 0.4);
      butt(-0.015, 0.004, 2.1);
      butt(0.002, 0.017, 3.6);

      // 림에 걸친 담배 한 개비 — 피우다 놓은 것. 탄 끝이 검다
      w.part('걸친 담배');
      const cig = new THREE.CylinderGeometry(0.0038, 0.0038, 0.075, 8);
      cig.rotateZ(Math.PI / 2);
      cig.rotateY(-0.4);
      cig.translate(-0.006, d.h * 0.95, -0.006);
      paintGeometry(cig, '#ece8de');
      w.add(cig, 'paint', 0.1);
      const tip = new THREE.CylinderGeometry(0.0038, 0.0038, 0.006, 8);
      tip.rotateZ(Math.PI / 2);
      tip.rotateY(-0.4);
      tip.translate(-0.006 + Math.cos(-0.4) * 0.04, d.h * 0.95, -0.006 - Math.sin(-0.4) * 0.04);
      paintGeometry(tip, '#2b2724');
      w.add(tip, 'paint', 0.1);
      const cf = new THREE.CylinderGeometry(0.004, 0.004, 0.02, 8);
      cf.rotateZ(Math.PI / 2);
      cf.rotateY(-0.4);
      cf.translate(-0.006 - Math.cos(-0.4) * 0.045, d.h * 0.95, -0.006 + Math.sin(-0.4) * 0.045);
      paintGeometry(cf, '#c49a58');
      w.add(cf, 'paint', 0.1);
    });
  },
});

/** 유선 전화 — 수화기를 들어 크래들 밑을 본다 */
SAHOL_PIECES.push({
  id: 'landline',
  mount: 'surface',
  name: '유선 전화',
  collection: 'sahol',
  category: '2026',
  note:
    '줄 달린 책상 전화. 수화기를 들어 보는 게 전화 수색이고, 수화기 밑에 쪽지를 끼워 두는 것도 ' +
    '오래된 습관이다. 외진 마을이라 유선이 아직 남은 게 자연스럽다.',
  dims: { w: 0.18, d: 0.21, h: 0.09 },
  /* 수화기를 들어 크래들에서 뗀다 — 밑에 끼인 것을 본다 */
  openable: [{ part: '수화기', slide: [0, 0.075, 0.02] }],
  build(w, d) {
    const baseH = d.h * 0.6;
    const hx = -d.w * 0.27;   // 수화기는 본체 왼쪽 크래들에 얹힌다
    w.part('본체');
    const base = bevelBox(d.w, baseH, d.d, 0.008, 2);
    base.translate(0, baseH / 2, 0);
    w.add(base, 'plastic', 0.3);
    // 크래들 받침 — 수화기가 놓이는 양쪽 턱 (수화기가 들리면 남는 것)
    for (const zz of [-0.062, 0.062]) {
      const hook = bevelBox(0.05, 0.016, 0.03, 0.004);
      hook.translate(hx, baseH + 0.008, zz);
      w.add(hook, 'plastic', 0.3);
    }
    // 경사 버튼면 — 뒤로 기운 얇은 판 위에 버튼이 선다
    w.part('버튼면');
    const face = bevelBox(d.w * 0.5, 0.008, d.d * 0.62, 0.002);
    face.rotateX(-0.18);
    face.translate(d.w * 0.22, baseH + 0.006, -0.005);
    w.add(face, 'plastic', 0.25);
    // 버튼 3×4 — 1m 밖에서 전화기로 읽히는 건 이 격자다.
    // 경사면을 따라 줄마다 y 를 내린다 — 평면으로 놓으면 앞 줄이 9mm 뜬다(joint-check)
    w.part('버튼');
    for (let r = 0; r < 4; r++) {
      const bz = -0.005 - 0.042 + r * 0.026;
      // 경사면 윗면의 높이: 면 중심(baseH+0.006) + 반두께·cos + z_local·tan
      const by = baseH + 0.006 + 0.0039 + 0.182 * (bz + 0.00572) + 0.0015;
      for (let c = 0; c < 3; c++) {
        const b = bevelBox(0.013, 0.005, 0.013, 0.0015);
        b.rotateX(-0.18);
        b.translate(d.w * 0.22 - 0.016 + c * 0.016, by, bz);
        paintGeometry(b, '#d8d5cf');
        w.add(b, 'paint', 0.15);
      }
    }
    // 전화선 — 본체 뒤에서 나와 책상 위로 늘어진다. 유선의 증거는 이 줄이다
    w.part('전화선');
    const line = new THREE.CatmullRomCurve3([
      new THREE.Vector3(d.w * 0.3, baseH * 0.7, d.d / 2 - 0.008),
      new THREE.Vector3(d.w * 0.44, 0.004, d.d / 2 + 0.05),
      new THREE.Vector3(d.w * 0.14, 0.004, d.d / 2 + 0.095),
    ]);
    w.thinSurface(() => w.add(new THREE.TubeGeometry(line, 12, 0.004, 6), 'rubber', 0.2));
    const plug = new THREE.CylinderGeometry(0.005, 0.005, 0.014, 8);
    plug.rotateX(Math.PI / 2);
    plug.translate(d.w * 0.14, 0.006, d.d / 2 + 0.098);
    w.add(plug, 'plastic', 0.2);

    /* 수화기 — 가운데가 얇고 귀·입이 굵은 막대. 꼬리 코드는 수화기와 같이 들린다 */
    w.movable('수화기', () => {
      const bar = bevelBox(0.046, 0.024, 0.15, 0.008, 2);
      bar.translate(hx, baseH + 0.032, 0);
      w.add(bar, 'plastic', 0.3);
      for (const zz of [-0.068, 0.068]) {
        const cup = bevelBox(0.052, 0.032, 0.046, 0.008, 2);
        cup.translate(hx, baseH + 0.034, zz);
        w.add(cup, 'plastic', 0.3);
      }
      // 꼬리 코드 — 들면 축 늘어져 본체로 향한다. 끝은 본체 옆면에 박힌다
      const cord = new THREE.CatmullRomCurve3([
        new THREE.Vector3(hx, baseH + 0.02, 0.095),
        new THREE.Vector3(hx - 0.045, baseH * 0.6, 0.1),
        new THREE.Vector3(hx - 0.02, baseH * 0.45, 0.1),
      ]);
      w.thinSurface(() => w.add(new THREE.TubeGeometry(cord, 10, 0.0035, 6), 'rubber', 0.2));
    });
  },
});

/** 구급함 — 초록 십자의 흰 상자. 뚜껑을 젖히면 붕대·소독약이 보인다 */
SAHOL_PIECES.push({
  id: 'first_aid',
  mount: 'surface',
  name: '구급함',
  collection: 'sahol',
  category: '2026',
  note:
    '흰 플라스틱 상자. **적십자 표장은 못 쓴다** — 제네바 협약상 적십자사 아니면 금지라 ' +
    '약국·가정 구급함은 초록 십자다. 상표 금지 규정과도 맞는다.',
  dims: { w: 0.27, d: 0.17, h: 0.16, t: 0.006 },
  /* 뚜껑은 뒤 윗모서리에서 뒤로 젖힌다. inside 는 상자 속 — 약품을 에디터가 넣을 수 있다 */
  openable: [
    { part: '뚜껑', swing: 1.9, axis: 'x', pivot: [0, 0.115, 0.085], inside: { w: 0.23, h: 0.09, d: 0.13, at: [0, 0.05, 0] } },
  ],
  build(w, d) {
    const bodyH = d.h - 0.045;   // 뚜껑 자리를 남긴 몸통 높이 (0.115)
    w.part('상자');
    for (const g of shellBox(d.w, bodyH, d.d, d.t, ['+y'])) w.add(g, 'plastic', 0.3);
    // 앞 래치 — 닫힘을 잡는 잠금쇠
    w.part('래치');
    const latch = bevelBox(0.04, 0.018, 0.012, 0.002);
    latch.translate(0, bodyH - 0.004, -d.d / 2 - 0.002);
    w.add(latch, 'plastic', 0.25);

    /* 안에 든 것 — 붕대·소독약·거즈가 보여야 구급함이다. 전부 상자 바닥에 놓인다 */
    w.part('붕대');
    for (const [x, z, r] of [
      [-0.07, 0.01, 0.032],
      [0.015, -0.02, 0.027],
    ]) {
      const roll = new THREE.CylinderGeometry(r, r, 0.05, 14);
      roll.translate(x, d.t + 0.025, z);
      w.add(roll, 'fabric', 0.4);
    }
    w.part('소독약');
    const bot = new THREE.CylinderGeometry(0.017, 0.017, 0.055, 12);
    bot.translate(0.085, d.t + 0.028, 0.02);
    paintGeometry(bot, '#7a5a3a');   // 갈색 소독약병
    w.add(bot, 'paint', 0.2);
    const cap = new THREE.CylinderGeometry(0.012, 0.012, 0.014, 10);
    cap.translate(0.085, d.t + 0.062, 0.02);
    w.add(cap, 'plastic', 0.3);
    w.part('거즈');
    const gauze = bevelBox(0.09, 0.02, 0.06, 0.002);
    gauze.translate(-0.02, d.t + 0.01, 0.032);
    w.add(gauze, 'paper', 0.4);

    /* 뚜껑은 닫힌 채로 — 윗면에 초록 십자 */
    w.movable('뚜껑', () => {
      const lid = bevelBox(d.w + 0.006, 0.04, d.d + 0.006, 0.006, 2);
      lid.translate(0, bodyH + 0.02, 0);
      w.add(lid, 'plastic', 0.3);
      const crossA = bevelBox(0.09, 0.006, 0.03, 0.001);
      const crossB = bevelBox(0.03, 0.006, 0.09, 0.001);
      for (const g of [crossA, crossB]) {
        g.translate(0, bodyH + 0.043, 0);
        paintGeometry(g, '#3d8b57');
        w.add(g, 'paint', 0.1);
      }
      const grip = bevelBox(0.06, 0.014, 0.015, 0.003);
      grip.translate(0, bodyH + 0.008, -d.d / 2 - 0.004);
      w.add(grip, 'plastic', 0.25);
    });
  },
});

/** 브라운관 TV — 뒤가 불룩하고 토끼귀 안테나. 몸체를 돌려 뒤를 본다 */
SAHOL_PIECES.push({
  id: 'crt_tv',
  name: '브라운관 TV',
  collection: 'sahol',
  category: '2026',
  note:
    '평면이 아니라 뒤가 불룩한 것 — 90년대 골조에 남은 브라운관. 외진 마을이라 아직 굴러간다. ' +
    'TV를 돌려 뒤(전원선·통풍구·뒤에 떨어뜨린 것)를 보는 게 수색의 기본이라 통째로 돌아간다.',
  dims: { w: 0.55, d: 0.47, h: 0.42 },
  /* 몸체를 통째로 돌려 뒤를 본다 — 앞 왼 모서리를 축으로 뒤가 방 안쪽(왼)으로 도는 방향.
   * −swing 이면 뒤 모서리가 벽 속으로 밀린다(벽에 붙여 놓으므로) — 양수여야 한다. */
  openable: [{ part: '본체', swing: 0.65, axis: 'y', pivot: [-0.2, 0, -0.18] }],
  build(w, d) {
    const footH = 0.035;
    const bodyH = d.h - footH;
    w.movable('본체', () => {
      w.part('받침');
      const foot = bevelBox(d.w * 0.6, footH, d.d * 0.55, 0.006);
      foot.translate(0, footH / 2, 0.02);
      w.add(foot, 'plastic', 0.3);

      w.part('몸통');
      // 앞이 크고 뒤로 갈수록 좁아지는 두 덩이 — 브라운관 실루엣의 전부다
      const front = bevelBox(d.w, bodyH * 0.94, d.d * 0.34, 0.02, 2);
      front.translate(0, footH + bodyH * 0.47, -d.d * 0.31);
      w.add(front, 'plastic', 0.35);
      const rear = bevelBox(d.w * 0.62, bodyH * 0.72, d.d * 0.6, 0.03, 2);
      rear.translate(0, footH + bodyH * 0.44, d.d * 0.16);
      w.add(rear, 'plastic', 0.35);

      w.part('화면');
      // 살짝 볼록한 꺼진 화면 — pane 은 transmission 없이 싸다. 꺼진 CRT 는 어두운 회색
      const scr = bevelBox(d.w * 0.76, bodyH * 0.7, 0.035, 0.012, 2);
      scr.translate(0, footH + bodyH * 0.54, -d.d * 0.465);
      paintGeometry(scr, '#14171c');
      w.add(scr, 'pane', 0.2);

      // 스피커 슬릿 + 버튼 — 앞면 아래 띠가 모니터와 TV를 가른다
      w.part('스피커');
      for (let i = 0; i < 5; i++) {
        const slit = bevelBox(0.09, 0.006, 0.008, 0.001);
        slit.translate(d.w * 0.28, footH + bodyH * 0.09 + i * 0.014, -d.d * 0.478);
        paintGeometry(slit, '#22252a');
        w.add(slit, 'paint', 0.15);
      }
      w.part('버튼');
      for (let i = 0; i < 3; i++) {
        const btn = new THREE.CylinderGeometry(0.008, 0.008, 0.012, 10);
        btn.rotateX(Math.PI / 2);
        btn.translate(-d.w * 0.3 + i * 0.033, footH + bodyH * 0.08, -d.d * 0.475);
        w.add(btn, 'plastic', 0.25);
      }

      // 통풍구 — 뒤통수를 TV 뒤로 만드는 검은 슬릿
      w.part('통풍구');
      for (let i = 0; i < 4; i++) {
        const vent = bevelBox(d.w * 0.36, 0.008, 0.006, 0.001);
        vent.translate(0, footH + bodyH * 0.6 + i * 0.02, d.d * 0.455);
        paintGeometry(vent, '#1d2025');
        w.add(vent, 'paint', 0.15);
      }

      w.part('안테나');
      // 뒷통 윗면(footH + bodyH·0.8 = 0.343)에 얹는다 — 0.94 에 놓으면 53mm 뜬다(joint-check)
      const antBase = new THREE.CylinderGeometry(0.02, 0.024, 0.015, 10);
      antBase.translate(0, footH + bodyH * 0.8 + 0.007, d.d * 0.08);
      w.add(antBase, 'plastic', 0.3);
      for (const s of [-1, 1]) {
        const rod = new THREE.CylinderGeometry(0.0028, 0.0038, 0.28, 8);
        rod.translate(0, 0.14, 0);
        rod.rotateZ(s * 0.45);
        rod.rotateX(-0.12);
        rod.translate(0, footH + bodyH * 0.8 + 0.012, d.d * 0.08);
        w.add(rod, 'alu', 0.3);
      }

      // 전원선 — 뒤 아래에서 나와 바닥에 늘어진다. 돌려도 같이 딸려 돈다
      w.part('전원선');
      const cord = new THREE.CatmullRomCurve3([
        new THREE.Vector3(d.w * 0.18, footH + 0.06, d.d * 0.44),
        new THREE.Vector3(d.w * 0.3, 0.006, d.d * 0.52),
        new THREE.Vector3(d.w * 0.1, 0.005, d.d * 0.57),
      ]);
      w.thinSurface(() => w.add(new THREE.TubeGeometry(cord, 10, 0.0045, 6), 'rubber', 0.25));
      const plug = bevelBox(0.02, 0.012, 0.03, 0.002);
      plug.translate(d.w * 0.1, 0.007, d.d * 0.58);
      w.add(plug, 'plastic', 0.2);
    });
  },
});
